// Trạng thái vỏ app (UI_SPEC §3–§4). Tab / màn đang mở và rail thu/mở lưu ở 'device';
// cửa sổ, trang con, dải hỏi, toast là phù du, không lưu.
import { signal, computed, batch } from '@preact/signals';
import { registerSlice } from '../registry';
import { isPlainObject } from '@save/types';
import { DEFAULT_TAB, TABS, isTabId, tabDef, type TabId } from '@data/screens';

export type SubpageId = 'self' | 'transmission' | 'bag' | 'tasks' | 'mail';

export type WindowEntry =
  | {
      kind: 'placeholder';
      /** Màn đã mở cửa này (để cửa đọc tên / giai đoạn). */
      screen: string;
    }
  | { kind: 'site' }
  | { kind: 'deathSeclusion' }
  | { kind: 'codex'; entryId: string }
  | { kind: 'item'; uid: string }
  | { kind: 'goods'; key: string }
  | { kind: 'filter' }
  | { kind: 'zone'; zoneId: string }
  | { kind: 'plot'; index: number }
  | { kind: 'building'; id: 'herbGarden' | 'pillRoom' }
  | { kind: 'alchemy' }
  | { kind: 'market'; mode: 'buy' | 'sell' };

export interface AskState {
  textKey: string;
  textParams?: Readonly<Record<string, string | number>>;
  confirmKey: string;
  /** Nút làm là nút GIỮ 720ms (bước vào Tử Quan, UI_SPEC §13). */
  hold?: boolean;
  onConfirm: () => void;
}

export interface ToastState {
  textKey: string;
  seq: number;
}

interface UiDevice {
  tab: TabId;
  screens: Record<TabId, string>;
  railCollapsed: boolean;
}

function defaultDevice(): UiDevice {
  return {
    tab: DEFAULT_TAB,
    screens: Object.fromEntries(TABS.map((t) => [t.id, t.screens[0]?.id ?? ''])) as Record<TabId, string>,
    railCollapsed: false,
  };
}

const _device = signal<UiDevice>(defaultDevice());
const _windows = signal<WindowEntry[]>([]);
const _subpage = signal<SubpageId | null>(null);
const _ask = signal<AskState | null>(null);
const _toast = signal<ToastState | null>(null);

export const activeTab = computed(() => _device.value.tab);
export const activeScreen = computed(() => _device.value.screens[_device.value.tab]);
export const screensByTab = computed(() => _device.value.screens);
export const railCollapsed = computed(() => _device.value.railCollapsed);
export const windows = computed(() => _windows.value);
export const subpage = computed(() => _subpage.value);
export const ask = computed(() => _ask.value);
export const toastState = computed(() => _toast.value);

export function selectTab(tab: TabId): void {
  batch(() => {
    _device.value = { ..._device.value, tab };
    _windows.value = [];
    _subpage.value = null;
  });
}

export function selectScreen(screen: string): void {
  const tab = _device.value.tab;
  if (!tabDef(tab).screens.some((s) => s.id === screen)) return;
  batch(() => {
    _device.value = { ..._device.value, screens: { ..._device.value.screens, [tab]: screen } };
    _windows.value = [];
  });
}

export function setRailCollapsed(v: boolean): void {
  _device.value = { ..._device.value, railCollapsed: v };
}

// ── Cửa sổ: stack; ‹ lui một nấc, ✕ đóng cả chuỗi.
export function openWindow(w: WindowEntry): void {
  _windows.value = [..._windows.value, w];
}
export function backWindow(): void {
  _windows.value = _windows.value.slice(0, -1);
}
export function closeWindows(): void {
  _windows.value = [];
}

// ── Trang con: bề mặt duy nhất được cuộn.
export function openSubpage(id: SubpageId): void {
  _subpage.value = id;
}
export function closeSubpage(): void {
  _subpage.value = null;
}

// ── Dải hỏi: 1 câu + 2 nút.
export function askConfirm(a: AskState): void {
  _ask.value = a;
}
export function resolveAsk(confirmed: boolean): void {
  const a = _ask.value;
  _ask.value = null;
  if (confirmed && a) a.onConfirm();
}

// ── Toast: CHỈ dành cho gacha (lint cấm gọi toast( ngoài src/ui/screens/companions/).
let toastSeq = 0;
export function toast(textKey: string): void {
  _toast.value = { textKey, seq: ++toastSeq };
}
export function clearToast(seq: number): void {
  if (_toast.value?.seq === seq) _toast.value = null;
}

registerSlice<UiDevice>({
  key: 'ui',
  scope: 'device',
  serialize: () => _device.value,
  hydrate: (d) => {
    const st = defaultDevice();
    if (isPlainObject(d)) {
      if (isTabId(d.tab)) st.tab = d.tab;
      if (isPlainObject(d.screens))
        for (const t of TABS) {
          const s = d.screens[t.id];
          if (typeof s === 'string' && t.screens.some((x) => x.id === s)) st.screens[t.id] = s;
        }
      if (typeof d.railCollapsed === 'boolean') st.railCollapsed = d.railCollapsed;
    }
    _device.value = st;
  },
});
